import React, { useState } from "react";
import moment from "moment";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TransferVolumeChart from "./TransferVolumeChart";
import TransferRecordDetails from "./TransferRecordDetails";
import { TYPE_META, chipSx, nestedSurfaceSx, useValueColor } from "./transferTheme";
import { formatDuration, formatGallons, signedGallons, transferDurationMs } from "./transferMath";

const cellSx = ({ palette, functions: { rgba } }) => ({
  px: 1.5,
  py: 1.25,
  textAlign: "left",
  borderBottom: `1px solid ${rgba(palette.text.main, 0.08)}`,
  fontSize: "0.8125rem",
  color: palette.text.main,
  whiteSpace: "nowrap",
});

const headSx = (theme) => ({
  ...cellSx(theme),
  fontSize: "0.625rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  backgroundColor: theme.functions.rgba(theme.palette.text.main, 0.04),
});

function SummaryTile({ label, value, color }) {
  const valueColor = useValueColor();
  return (
    <Paper elevation={0} sx={(theme) => ({ ...nestedSurfaceSx(theme), p: 1.5 })}>
      <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
        {label}
      </MDTypography>
      <MDTypography variant="h5" color={color ?? valueColor} fontWeight="bold">
        {value}
      </MDTypography>
    </Paper>
  );
}

const rate = (transfer) => {
  const ms = transferDurationMs(transfer);
  return ms && transfer.gallonsTransferred ? `${Math.round(transfer.gallonsTransferred / (ms / 60000))} gpm` : "--";
};

export default function TransferDayTable({ day, session, completedSamples = {}, capacity, onRefresh }) {
  const [openId, setOpenId] = useState(null);
  const { summary, transfers, status } = day;
  const total = summary.totalIn + summary.totalOut || 1;
  const liveRow = session?.status === "active" || session?.status === "saving";

  return (
    <MDBox>
      <Grid container spacing={1} mb={1.5}>
        <Grid item xs={6} md><SummaryTile label="Total in" value={formatGallons(summary.totalIn)} color="info" /></Grid>
        <Grid item xs={6} md><SummaryTile label="Total out" value={formatGallons(summary.totalOut)} color="primary" /></Grid>
        <Grid item xs={6} md><SummaryTile label="Net" value={formatGallons(summary.netGallons, { signed: true })} /></Grid>
        <Grid item xs={6} md><SummaryTile label="Time transferring" value={formatDuration(summary.totalTimeSeconds * 1000)} /></Grid>
        <Grid item xs={12} md><SummaryTile label="Transfers" value={summary.transferCount} /></Grid>
      </Grid>

      <MDBox
        display="flex"
        height={10}
        mb={2}
        sx={({ palette, functions: { rgba } }) => ({ borderRadius: "999px", overflow: "hidden", backgroundColor: rgba(palette.text.main, 0.1) })}
      >
        <MDBox bgColor="info" width={`${(summary.totalIn / total) * 100}%`} sx={{ transition: "width .6s" }} />
        <MDBox bgColor="primary" width={`${(summary.totalOut / total) * 100}%`} sx={{ transition: "width .6s" }} />
      </MDBox>

      {status === "unavailable" && (
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1.5}>
          <MDTypography variant="button" color="warning">
            Transfer history unavailable. Live transfer tracking still works.
          </MDTypography>
          <MDButton size="small" variant="outlined" color="info" onClick={onRefresh}>
            Retry
          </MDButton>
        </MDBox>
      )}

      <MDBox sx={({ palette, functions: { rgba } }) => ({ overflowX: "auto", border: `1px solid ${rgba(palette.text.main, 0.08)}`, borderRadius: "12px" })}>
        <MDBox component="table" width="100%" sx={{ borderCollapse: "collapse", minWidth: 640 }}>
          <MDBox component="thead">
            <MDBox component="tr">
              {["Started", "Type", "Validation", "Start → Stop", "Gallons", "Duration", "Avg rate"].map((heading) => (
                <MDBox component="th" key={heading} sx={headSx}>{heading}</MDBox>
              ))}
            </MDBox>
          </MDBox>
          <MDBox component="tbody">
            {liveRow && (
              <MDBox component="tr" sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette.info.main, 0.08) })}>
                <MDBox component="td" sx={cellSx}>{moment(session.startedAt).format("h:mm A")}</MDBox>
                <MDBox component="td" sx={cellSx}>
                  <Chip size="small" color="warning" sx={chipSx} label={session.status === "saving" ? "SAVING" : session.direction.toUpperCase()} />
                </MDBox>
                <MDBox component="td" sx={cellSx}>--</MDBox>
                <MDBox component="td" sx={cellSx}>{formatGallons(session.baseline)} → {formatGallons(session.gallons)}</MDBox>
                <MDBox component="td" sx={cellSx}><b>{formatGallons(session.moved, { signed: true })}</b> est.</MDBox>
                <MDBox component="td" sx={cellSx}>{formatDuration((session.endedAt ?? Date.now()) - Date.parse(session.startedAt))}</MDBox>
                <MDBox component="td" sx={cellSx}>{Math.abs(session.gpm) >= 1 ? `${Math.round(Math.abs(session.gpm))} gpm` : "--"}</MDBox>
              </MDBox>
            )}

            {[...transfers].reverse().map((transfer) => {
              const meta = TYPE_META[transfer.type] ?? TYPE_META.no_change;
              const open = openId === transfer.id;
              const samples = completedSamples[transfer.id];
              const validationColor = {
                valid: "success",
                warning: "warning",
                invalid: "error",
                pending: "info",
                open: "info",
                cancelled: "default",
                voided: "error",
              }[transfer.validationStatus] ?? "default";

              return (
                <React.Fragment key={transfer.id}>
                  <MDBox
                    component="tr"
                    onClick={() => setOpenId(open ? null : transfer.id)}
                    sx={({ palette, functions: { rgba } }) => ({ cursor: "pointer", "&:hover": { backgroundColor: rgba(palette.text.main, 0.05) } })}
                  >
                    <MDBox component="td" sx={cellSx}>{moment(transfer.startedAt).format("h:mm A")}</MDBox>
                    <MDBox component="td" sx={cellSx}>
                      <Chip size="small" color={meta.color} sx={chipSx} icon={<i className={meta.icon} style={{ fontSize: 10 }} />} label={meta.label.toUpperCase()} />
                    </MDBox>
                    <MDBox component="td" sx={cellSx}>
                      <Chip size="small" color={validationColor} sx={chipSx} label={transfer.validationStatus?.toUpperCase() ?? "PENDING"} />
                    </MDBox>
                    <MDBox component="td" sx={cellSx}>{formatGallons(transfer.startGallons)} → {formatGallons(transfer.stopGallons)}</MDBox>
                    <MDBox component="td" sx={cellSx}>
                      {transfer.gallonsTransferred != null 
                        ? <b>{formatGallons(signedGallons(transfer), { signed: true })}</b> 
                        : <MDTypography variant="caption" color="text">-- (invalid)</MDTypography>
                      }
                    </MDBox>
                    <MDBox component="td" sx={cellSx}>{formatDuration(transferDurationMs(transfer))}</MDBox>
                    <MDBox component="td" sx={cellSx}>{rate(transfer)}</MDBox>
                  </MDBox>
                  <MDBox component="tr">
                    <MDBox component="td" colSpan={7} sx={{ p: 0, border: 0 }}>
                      <Collapse in={open} unmountOnExit>
                        <MDBox p={2}>
                          <TransferRecordDetails transfer={transfer} />
                          {samples?.length > 1 && (
                            <MDBox mt={2}>
                              <TransferVolumeChart samples={samples} baseline={transfer.startGallons} capacity={capacity} direction={transfer.type} height={160} />
                            </MDBox>
                          )}
                        </MDBox>
                      </Collapse>
                    </MDBox>
                  </MDBox>
                </React.Fragment>
              );
            })}

            {!liveRow && transfers.length === 0 && (
              <MDBox component="tr">
                <MDBox component="td" colSpan={6} sx={cellSx}>
                  {status === "loading" ? "Loading transfers…" : "No transfers yet today"}
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
